package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.components.CommandPaletteState
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.i18n.t

@Composable
internal fun EditorScreenContent(
    state: TranslationEditorState,
    controller: EditorControllerState,
    compactWorkspaceClientWidth: Double,
    applyControllerTransition: (String, EditorTransition) -> Unit,
    onRetryFoundation: () -> Unit,
    onShowQueue: (EditorFocusRef?) -> Unit,
    onOpenFilters: (EditorFocusRef) -> Unit,
    onCloseFilters: () -> Unit,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit,
    onCloseUtility: (EditorUtilityActionOrigin) -> Unit,
    onSwitchUtility: (EditorUtilitySurface, EditorUtilityActionOrigin) -> Unit,
    onAdvanceUtilityPresence: (Long) -> Unit,
    onCompleteUtilityExit: (Long) -> Unit,
    onOpenModal: (EditorModalSurface, EditorFocusRef) -> Unit,
    onCloseModal: () -> Unit,
    onOpenOverflow: (EditorFocusRef) -> Unit,
    onCloseOverflow: () -> Unit,
    onCopySource: () -> Unit,
    onInsertText: (String) -> Unit,
    onInsertSuggestion: (EditorSuggestionInsertion, EditorUtilityActionOrigin) -> Unit,
    currentActivationOrigin: () -> EditorUtilityActionOrigin,
    discardActivationOrigin: () -> Unit,
    onRecordQueueScroll: (Double) -> Unit,
    onQueueRowsApplied: (Long) -> Unit,
    onSelectKey: (String) -> Unit,
    onRequestUtilityFocus: (EditorFocusRef, EditorFocusRef?) -> Unit,
) {
    val workspace = controller.workspace
    val foundationFailed = state.foundationError != null && state.project == null
    val plan = editorCompositionPlan(workspace, foundationFailed)
    val rowsRevision = state.rowsAppliedQueryRevision.takeUnless {
        workspace.measuredLayoutDisposition == EditorMeasuredLayoutDisposition.MEASURING
    }
    val showTargetPreview = showQueueTargetPreview(
        renderedLayout = workspace.renderedLayout(),
        queueMounted = workspace.mountsQueueList(),
        compactWorkspaceClientWidth = compactWorkspaceClientWidth,
    )
    EditorComposition(
        workspace = workspace,
        plan = plan,
        // The same sentence the header shows, as the page's h1. Composed here rather than read
        // from the header because the heading has to exist even while the header is a skeleton.
        documentHeading = state.project?.let { project ->
            "${project.name} · ${localeInfo(state.locale).englishName}"
        },
        announcement = controller.announcement,
        foundationFailure = if (foundationFailed) {
            {
                ErrorState(
                    message = state.foundationError.orEmpty(),
                    title = t("editor.editor_screen_content.translation_editor_unavailable", "Translation editor unavailable"),
                    onRetry = onRetryFoundation,
                )
            }
        } else {
            null
        },
        header = {
            EditorHeader(
                state = state,
                workspace = workspace,
                onShowQueue = onShowQueue,
                onOpenUtility = onOpenUtility,
                onOpenOverflow = onOpenOverflow,
                onCloseOverflow = onCloseOverflow,
                onOpenModal = onOpenModal,
            )
        },
        queue = {
            KeyListPane(
                state = state,
                queueMode = workspace.queueMode,
                renderedLayout = workspace.renderedLayout(),
                persistedScrollTopPx = workspace.queueScrollTopPx,
                showTargetPreview = showTargetPreview,
                rowsAppliedRevision = rowsRevision,
                currentQueryRevision = workspace.queueQueryRevision,
                onRecordScroll = onRecordQueueScroll,
                onRowsApplied = onQueueRowsApplied,
                onSelectKey = onSelectKey,
                onOpenFilters = onOpenFilters,
                onCloseFilters = onCloseFilters,
                onOpenBulkConfirm = { invoker ->
                    onOpenModal(EditorModalSurface.BULK_CONFIRMATION, invoker)
                },
            )
        },
        editor = {
            TranslationPane(
                state = state,
                compactActions = workspace.viewportWidth < EDITOR_NARROW_HEADER_BREAKPOINT_PX,
                sidePaddingPx = workspace.editorSidePaddingPx,
                onCopySource = onCopySource,
                onInsertText = onInsertText,
                onOpenUtility = onOpenUtility,
                onOpenModal = onOpenModal,
                onOpenOverflow = onOpenOverflow,
                onCloseOverflow = onCloseOverflow,
            )
        },
        utility = { shell ->
            EditorUtilityPane(
                state = state,
                surface = workspace.utility,
                shell = shell,
                onSelectSurface = onSwitchUtility,
                onRequestClose = onCloseUtility,
                onAdvancePresence = onAdvanceUtilityPresence,
                onCompleteExit = onCompleteUtilityExit,
                onRequestFocus = onRequestUtilityFocus,
                onSuggestionInserted = onInsertSuggestion,
                currentActivationOrigin = currentActivationOrigin,
                discardActivationOrigin = discardActivationOrigin,
                terminalFeedbackOccluded =
                    workspace.modal != EditorModalSurface.NONE || CommandPaletteState.open,
            )
        },
        modal = {
            EditorModalSurfaces(
                modal = workspace.modal,
                onClose = onCloseModal,
                capability = state.capability,
                automation = state.queueAutomation,
                onConfirmClear = {
                    if (state.capability.canWriteTranslations) {
                        state.clearCurrent()
                        onCloseModal()
                    }
                },
            )
        },
    )
    ToastHost(state = state.toasts)
}
